import { useEffect, useState, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import api from './lib/api';
import { Toaster, toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import {
  Gamepad2, Wallet, User, Shield, Bell, LogOut, Menu, X, History, BookOpen, Sparkles
} from 'lucide-react';
import { UserAvatar } from './components/UserAvatar';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import WalletPage from './pages/WalletPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import SubAdminPage from './pages/SubAdminPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BetHistoryPage from './pages/BetHistoryPage';
import NotificationsPage from './pages/NotificationsPage';
import HelplineWidget from './components/HelplineWidget';
import RulesPage from './pages/RulesPage';
import AuthCallback from './pages/AuthCallback';

const THEMES = {
  1: { bg: '#0A0A0E', surface: '#13131A', elevated: '#1C1C26', primary: '#05D9E8', secondary: '#FF2A6D', accent: '#FFD700', text: '#FFFFFF', textSec: '#A0A0B0', textMuted: '#6E6E80' },
  2: { bg: '#0A0812', surface: '#151020', elevated: '#1F1A2E', primary: '#FFD700', secondary: '#9B59B6', accent: '#E74C3C', text: '#FFFFFF', textSec: '#B0A0C0', textMuted: '#6E5E80' },
  3: { bg: '#0A1014', surface: '#101820', elevated: '#18222E', primary: '#00B4D8', secondary: '#0077B6', accent: '#90E0EF', text: '#FFFFFF', textSec: '#A0B0C0', textMuted: '#6E7E8E' },
  4: { bg: '#120A08', surface: '#1A1210', elevated: '#261A16', primary: '#FF6B35', secondary: '#F72585', accent: '#FFD166', text: '#FFFFFF', textSec: '#C0A090', textMuted: '#806E60' },
  5: { bg: '#080E0A', surface: '#101A12', elevated: '#18261A', primary: '#00C853', secondary: '#69F0AE', accent: '#FFD700', text: '#FFFFFF', textSec: '#A0C0A0', textMuted: '#6E806E' },
  6: { bg: '#120808', surface: '#1A1010', elevated: '#261616', primary: '#FF1744', secondary: '#FF6B6B', accent: '#FFD700', text: '#FFFFFF', textSec: '#C0A0A0', textMuted: '#806E6E' },
  7: { bg: '#E8EEF2', surface: '#F0F4F8', elevated: '#FFFFFF', primary: '#0277BD', secondary: '#00897B', accent: '#FF6F00', text: '#1A1A1A', textSec: '#4A4A5A', textMuted: '#8A8A9A' },
  8: { bg: '#0A0A1E', surface: '#10102A', elevated: '#181838', primary: '#536DFE', secondary: '#7C4DFF', accent: '#FFD740', text: '#FFFFFF', textSec: '#A0A0C0', textMuted: '#6E6E90' },
  9: { bg: '#100A06', surface: '#1A1208', elevated: '#261C10', primary: '#FF8F00', secondary: '#D84315', accent: '#FFC107', text: '#FFFFFF', textSec: '#C0B0A0', textMuted: '#807060' },
  10: { bg: '#0E0A14', surface: '#16101E', elevated: '#201828', primary: '#AB47BC', secondary: '#7E57C2', accent: '#FF80AB', text: '#FFFFFF', textSec: '#B0A0C0', textMuted: '#706080' },
};

const FONTS = {
  1: { heading: "'Unbounded', sans-serif", body: "'Outfit', sans-serif" },
  2: { heading: "'Rajdhani', sans-serif", body: "'Exo 2', sans-serif" },
  3: { heading: "'Orbitron', sans-serif", body: "'Roboto', sans-serif" },
  4: { heading: "'Chakra Petch', sans-serif", body: "'Nunito', sans-serif" },
  5: { heading: "'Teko', sans-serif", body: "'Poppins', sans-serif" },
};

function applyTheme(themeId) {
  const t = THEMES[themeId] || THEMES[1];
  const r = document.documentElement.style;
  r.setProperty('--neon-bg', t.bg);
  r.setProperty('--neon-surface', t.surface);
  r.setProperty('--neon-elevated', t.elevated);
  r.setProperty('--neon-primary', t.primary);
  r.setProperty('--neon-secondary', t.secondary);
  r.setProperty('--neon-accent', t.accent);
  r.setProperty('--neon-text', t.text);
  r.setProperty('--neon-text-secondary', t.textSec);
  r.setProperty('--neon-text-muted', t.textMuted);
  r.setProperty('--neon-primary-glow', t.primary + '80');
  r.setProperty('--neon-secondary-glow', t.secondary + '80');
}

function applyFont(fontId) {
  const f = FONTS[fontId] || FONTS[1];
  const r = document.documentElement.style;
  r.setProperty('--font-heading', f.heading);
  r.setProperty('--font-body', f.body);
}

function ProtectedRoute({ children, adminOnly = false, subAdminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--neon-bg)' }}>
      <div className="text-lg font-bold pulse-glow p-4 rounded-xl neon-border" style={{ color: 'var(--neon-primary)' }}>
        Loading...
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" />;
  if (subAdminOnly && user.role !== 'sub_admin') return <Navigate to="/" />;
  return children;
}

function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      api.get('/notifications/unread-count').then(r => setUnreadCount(r.data.count)).catch(() => {});
      const interval = setInterval(() => {
        api.get('/notifications/unread-count').then(r => setUnreadCount(r.data.count)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  if (!user) return null;

  const navItems = [
    { path: '/', icon: Gamepad2, label: 'Game' },
    { path: '/bets', icon: History, label: 'Bets' },
    { path: '/wallet', icon: Wallet, label: 'Wallet' },
    { path: '/rules', icon: BookOpen, label: 'Result & Rule' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];
  if (user.role === 'admin') {
    navItems.push({ path: '/admin', icon: Shield, label: 'Admin' });
  }
  if (user.role === 'sub_admin') {
    navItems.push({ path: '/subadmin', icon: Shield, label: 'Panel' });
  }

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    navigate('/login');
  };

  const markRead = async () => {
    try {
      await api.put('/notifications/mark-read');
      setUnreadCount(0);
    } catch {}
  };

  const isAdmin = user.role === 'admin' || user.role === 'sub_admin';

  return (
    <>
      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="border-none max-w-sm" style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-secondary)' }}>
              Confirm Logout
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm py-2" style={{ color: 'var(--neon-text-secondary)' }}>
            Are you sure you want to logout from Mukamari?
          </p>
          <div className="flex gap-3">
            <button data-testid="confirm-logout-yes" onClick={confirmLogout}
              className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ background: 'rgba(255,42,109,0.15)', color: '#FF2A6D' }}>
              Yes, Logout
            </button>
            <button data-testid="confirm-logout-no" onClick={() => setShowLogoutConfirm(false)}
              className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-text-secondary)' }}>
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Desktop Nav - Only for Admin/Sub-Admin */}
      {isAdmin && (
        <nav className="sticky top-0 z-50 hidden md:flex items-center justify-between px-6 py-3"
          style={{ background: 'rgba(10,10,14,0.9)', backdropFilter: 'blur(24px)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Link to="/" className="text-xl font-black brand-glow" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
          Mukamari
        </Link>
        <div className="flex items-center gap-1">
          {navItems.map(item => (
            <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${location.pathname === item.path ? 'neon-border' : ''}`}
              style={{ color: location.pathname === item.path ? 'var(--neon-primary)' : 'var(--neon-text-secondary)' }}>
              <item.icon size={16} /> {item.label}
            </Link>
          ))}
          <Link to="/notifications" data-testid="notifications-btn" onClick={markRead}
            className="relative p-2 rounded-lg transition-all hover:scale-110"
            style={{ color: 'var(--neon-text-secondary)' }}>
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                style={{ background: 'var(--neon-secondary)', color: '#fff' }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Link>
          <button data-testid="logout-btn" onClick={handleLogout}
            className="p-2 rounded-lg transition-all hover:scale-110"
            style={{ color: 'var(--neon-text-muted)' }}>
            <LogOut size={18} />
          </button>
        </div>
      </nav>
      )}

      {/* Mobile Bottom Nav - always visible for users, md:hidden for admin */}
      <nav className={`fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around pt-2 pb-12 px-2 ${isAdmin ? 'md:hidden' : 'user-mobile-nav'}`}
        style={{ background: 'var(--neon-bg)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        {navItems.map(item => (
          <Link key={item.path} to={item.path} data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs transition-all"
            style={{ color: location.pathname === item.path ? 'var(--neon-primary)' : 'var(--neon-text-muted)' }}>
            <item.icon size={20} />
            <span className="font-bold">{item.label}</span>
          </Link>
        ))}
        <Link to="/notifications" data-testid="mobile-notifications-btn" onClick={markRead}
          className="relative flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs"
          style={{ color: location.pathname === '/notifications' ? 'var(--neon-primary)' : 'var(--neon-text-muted)' }}>
          <Bell size={20} />
          <span className="font-bold">Alerts</span>
          {unreadCount > 0 && (
            <span className="absolute top-0 right-1 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center"
              style={{ background: 'var(--neon-secondary)', color: '#fff' }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Link>
        <button data-testid="mobile-logout-btn" onClick={handleLogout}
          className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs"
          style={{ color: 'var(--neon-text-muted)' }}>
          <LogOut size={20} />
          <span className="font-bold">Exit</span>
        </button>
      </nav>
    </>
  );
}

function PopupHandler() {
  const { user } = useAuth();
  const [popups, setPopups] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loginPopup, setLoginPopup] = useState(null);
  const [animClass, setAnimClass] = useState('popup-animate-in');

  useEffect(() => {
    if (user) {
      const hasSeenLoginPopup = sessionStorage.getItem('neonplay_login_popup_seen');
      if (!hasSeenLoginPopup) {
        api.get('/settings/login-popup').then(r => {
          if (r.data.active && r.data.title) {
            setLoginPopup(r.data);
            sessionStorage.setItem('neonplay_login_popup_seen', 'true');
          }
        }).catch(() => {});
      }

      api.get('/notifications/popups').then(r => {
        if (r.data.popups?.length > 0) {
          setPopups(r.data.popups);
          if (!loginPopup) setCurrent(r.data.popups[0]);
        }
      }).catch(() => {});
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const animations = ['popup-bounce', 'popup-slide-up', 'popup-zoom', 'popup-fade-rotate', 'popup-swing'];
  const getRandomAnim = () => animations[Math.floor(Math.random() * animations.length)];

  const dismissLoginPopup = () => {
    setAnimClass('popup-animate-out');
    setTimeout(() => {
      setLoginPopup(null);
      setAnimClass(getRandomAnim());
      if (popups.length > 0) setCurrent(popups[0]);
    }, 300);
  };

  useEffect(() => {
    if (loginPopup) {
      setAnimClass(getRandomAnim());
      const timer = setTimeout(dismissLoginPopup, 8000);
      return () => clearTimeout(timer);
    }
  }, [loginPopup]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = async () => {
    if (current?.id) {
      try { await api.put(`/notifications/popup-read/${current.id}`); } catch {}
    }
    setAnimClass('popup-animate-out');
    setTimeout(() => {
      const remaining = popups.filter(p => p.id !== current?.id);
      setPopups(remaining);
      setAnimClass(getRandomAnim());
      setCurrent(remaining.length > 0 ? remaining[0] : null);
    }, 300);
  };

  // Show login popup first
  if (loginPopup) {
    return (
      <Dialog open={true} onOpenChange={() => dismissLoginPopup()}>
        <DialogContent className={`glass-card border-none max-w-md ${animClass}`}
          style={{ background: 'var(--neon-surface)', borderTop: '3px solid var(--neon-accent)' }}
          aria-describedby="login-popup-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)' }}>
              <span className="inline-block w-8 h-8 rounded-full flex items-center justify-center text-sm"
                style={{ background: 'rgba(255,215,0,0.15)' }}>
                <Sparkles size={16} style={{ color: 'var(--neon-accent)' }} />
              </span>
              {loginPopup.title}
            </DialogTitle>
          </DialogHeader>
          <p id="login-popup-desc" className="text-sm py-4 leading-relaxed" style={{ color: 'var(--neon-text-secondary)' }}>{loginPopup.message}</p>
          <button data-testid="dismiss-login-popup-btn" onClick={dismissLoginPopup}
            className="btn-neon-primary w-full">OK</button>
        </DialogContent>
      </Dialog>
    );
  }

  if (!current) return null;
  return (
    <Dialog open={!!current} onOpenChange={() => dismiss()}>
      <DialogContent className={`glass-card border-none max-w-md ${animClass}`}
        style={{ background: 'var(--neon-surface)', borderTop: '3px solid var(--neon-primary)' }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
            <span className="inline-block w-8 h-8 rounded-full flex items-center justify-center text-sm"
              style={{ background: 'rgba(5,217,232,0.15)' }}>
              <Bell size={16} style={{ color: 'var(--neon-primary)' }} />
            </span>
            {current.title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm py-4 leading-relaxed" style={{ color: 'var(--neon-text-secondary)' }}>{current.message}</p>
        <button data-testid="dismiss-popup-btn" onClick={dismiss}
          className="btn-neon-primary w-full">OK</button>
      </DialogContent>
    </Dialog>
  );
}

// Mobile-only wrapper for user pages (centers content in phone-width on desktop)
function MobileLayout({ children }) {
  const { user } = useAuth();
  // Admin and Sub-Admin get full responsive layout
  if (user?.role === 'admin' || user?.role === 'sub_admin') return children;
  return (
    <div className="mobile-only-wrapper">
      {children}
    </div>
  );
}

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    api.get('/settings').then(r => {
      applyTheme(r.data.theme?.theme_id || 1);
      applyFont(r.data.font?.font_id || 1);
    }).catch(() => {});
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Detect session_id synchronously during render to handle Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isAdminRoute = location.pathname === '/admin' || location.pathname === '/subadmin';

  return (
    <>
      <Navbar />
      <PopupHandler />
      {isAdminRoute ? (
        <Routes>
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
          <Route path="/subadmin" element={<ProtectedRoute subAdminOnly><SubAdminPage /></ProtectedRoute>} />
        </Routes>
      ) : (
        <MobileLayout>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
            <Route path="/bets" element={<ProtectedRoute><BetHistoryPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/rules" element={<ProtectedRoute><RulesPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </MobileLayout>
      )}
    </>
  );
}

function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner only if not already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div data-testid="install-prompt" className="fixed top-0 left-0 right-0 z-[100] p-3 flex items-center justify-between"
      style={{ background: 'linear-gradient(135deg, rgba(5,217,232,0.95), rgba(83,109,254,0.95))', backdropFilter: 'blur(10px)' }}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold" style={{ color: '#000' }}>Install Mukamari App</span>
      </div>
      <div className="flex gap-2">
        <button onClick={handleInstall} className="px-4 py-1.5 rounded-lg text-xs font-bold"
          style={{ background: '#000', color: '#05D9E8' }}>
          Install
        </button>
        <button onClick={() => setShowBanner(false)} className="px-2 py-1.5 rounded-lg text-xs font-bold"
          style={{ background: 'rgba(0,0,0,0.2)', color: '#000' }}>
          Later
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors theme="dark" />
        <InstallPrompt />
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
